import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GripVertical, Plus, Search } from 'lucide-react'
import { PRIORITY_ICONS } from './priority'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { cn } from '@/lib/utils'

/** DataTransfer type used when dragging a backlog task onto a cycle. */
export const CYCLE_TASK_DRAG_TYPE = 'application/x-moly-task-id'

interface CycleBacklogPanelProps {
  /** Open project tasks not planned into any open cycle. */
  tasks: Array<TaskDocument>
  onAdd: (taskId: string) => void
  resolveIdentifier: (task: TaskDocument) => string
  projectName?: string
}

/**
 * The backlog beside a draft cycle (design 8b) — replaces the Add Tasks
 * search popup. Drag a task across, or press + to add it.
 */
export function CycleBacklogPanel({ tasks, onAdd, resolveIdentifier, projectName }: CycleBacklogPanelProps) {
  const { t } = useTranslation('projects')
  const [query, setQuery] = useState('')

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? tasks.filter((task) => task.name.toLowerCase().includes(q)) : tasks
  }, [tasks, query])

  return (
    <aside className="flex min-h-0 flex-col rounded-xl border bg-card">
      <div className="border-b px-4 pb-3 pt-3.5">
        <div className="mb-2.5 flex items-baseline justify-between gap-2">
          <h3 className="text-[13px] font-semibold">
            {t('cycles.backlog')}
            {projectName && <span className="font-normal text-muted-foreground"> · {projectName}</span>}
          </h3>
          <span className="text-xs tabular-nums text-muted-foreground">{tasks.length}</span>
        </div>
        <label className="flex h-8 items-center gap-2 rounded-md bg-secondary px-2.5 text-base md:text-[13px]">
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('cycles.filterBacklog')}
            className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          />
        </label>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto py-1">
        {visible.length === 0 && (
          <li className="px-4 py-6 text-center text-xs text-muted-foreground">{t('cycles.backlogEmpty')}</li>
        )}
        {visible.map((task) => {
          const priority = PRIORITY_ICONS[task.priority ?? 0]
          return (
            <li
              key={task.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(CYCLE_TASK_DRAG_TYPE, task.id)
                e.dataTransfer.effectAllowed = 'copy'
              }}
              className="group flex cursor-grab items-center gap-2 px-3 py-2 hover:bg-secondary/50 active:cursor-grabbing"
            >
              <GripVertical className="size-3.5 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground" />
              <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground">{resolveIdentifier(task)}</span>
              <span className="min-w-0 flex-1 truncate text-[13px]">{task.name}</span>
              {task.priority != null && task.priority > 0 && priority && (
                <priority.Icon className={cn('size-3.5 shrink-0', priority.color)} />
              )}
              <button
                type="button"
                onClick={() => onAdd(task.id)}
                aria-label={t('cycles.addToCycle')}
                title={t('cycles.addToCycle')}
                className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-60 transition hover:bg-secondary hover:text-foreground group-hover:opacity-100"
              >
                <Plus className="size-3.5" />
              </button>
            </li>
          )
        })}
      </ul>
      <p className="border-t px-4 py-2 text-[11px] text-muted-foreground">{t('cycles.dragHint')}</p>
    </aside>
  )
}
